import React, { useRef } from "react";
// TinyMCE so the global var exists

import { Editor } from "@tinymce/tinymce-react";

// TinyMCE so the global var exists
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
import "tinymce/tinymce";
// DOM model
import "tinymce/models/dom/model";
// Theme
import "tinymce/themes/silver";
// Toolbar icons
import "tinymce/icons/default";
// Editor styles
import "tinymce/skins/ui/oxide/skin.min.css";

// importing the plugin js.
// if you use a plugin that is not listed here the editor will fail to load
import "tinymce/plugins/image";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/table";
import "tinymce/plugins/autoresize";
import "tinymce/plugins/preview";
import "tinymce/plugins/emoticons";

// importing plugin resources
import "tinymce/plugins/emoticons/js/emojis";

/* content UI CSS is required */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import contentUiCss from "tinymce/skins/ui/oxide/content.min.css?raw";

/* The default content CSS can be changed or replaced with appropriate CSS for the editor content. */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import contentCss from "tinymce/skins/content/default/content.min.css?raw";
import { RichTextInput } from "@custom-types/input-type";

import RecordAvatar from "@components/TinyComponents/RecordAvatar";
import ReactDOMServer from "react-dom/server";
import icons from "../../assets/icons/tinymce-icons";

import "./styles/index.scss";
import "./styles/tinyStyle.scss";

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
tinymce.IconManager.add("small", { icons });

const mimeTypes = [
	"image/jpeg",
	"image/jpg",
	"image/png",
	"image/tiff",
	"image/vnd.wap.wbmp",
	"image/x-icon",
	"image/x-jng",
	"image/x-ms-bmp",
	"image/svg+xml",
	"image/webp",
];

function TinyMceInput(props: RichTextInput): JSX.Element {
	const {
		value,
		customButtons = [],
		disable = false,
		inline = false,
		height = 150,
	} = props;
	const editorRef = useRef(null);

	const handleEditorChange = (): void => {
		const { attachments = [] } = value || {};
		if (editorRef.current) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			const text = editorRef.current.getContent({ format: "text" });
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			const html = editorRef.current.getContent();

			// Extract mention IDs from the HTML content
			const tempDiv = document.createElement("div");
			tempDiv.innerHTML = html;

			// Find all mention spans
			const mentionSpans = tempDiv.querySelectorAll("span.mention");

			// Process each mention span
			mentionSpans.forEach(span => {
				const name = span.getAttribute("data-mention-name");
				const expectedText = `@${name}`;

				// If the content doesn't match exactly, remove the mention formatting
				if (span.textContent !== expectedText) {
					// Remove mention attributes but keep the content
					span.classList.remove("mention");
					span.removeAttribute("data-mention-id");
					span.removeAttribute("data-mention-name");
				}
			});

			// Get the updated HTML after potential modifications
			const updatedHtml = tempDiv.innerHTML;

			// Re-query for valid mentions only
			const validMentions = tempDiv.querySelectorAll("span.mention");
			const mentionIds = Array.from(validMentions)
				.map(span => span.getAttribute("data-mention-id"))
				.filter(Boolean);

			const newValue = {
				html: updatedHtml, // Use the updated HTML
				text,
				attachments,
				mentions: mentionIds,
			};

			const record = {
				fieldId: props.fieldId,
				fieldName: props.fieldName,
				sectionId: props.sectionId,
				value: newValue,
				typeCode: props.typeCode,
				isRequired: props.isRequired,
			};
			props.handleChange?.(record);
		}
	};

	const handleBlur = (): void => {
		const record = {
			fieldId: props.fieldId,
			fieldName: props.fieldName,
			sectionId: props.sectionId,
			value: props.value,
			typeCode: props.typeCode,
			isRequired: props.isRequired,
		};
		props.handleBlur(record);
	};

	const getCustomToolBarButtons = (): string =>
		customButtons.map(btn => btn.name).join(" ");

	const getToolBar = React.useMemo<string | boolean>((): string | boolean => {
		let toolbar = `font fontstyle allignment lists ${
			!props.allowMentions ? "table link image hr removeformat preview" : ""
		} emoticons ${getCustomToolBarButtons()}`;
		if (props.moduleId === "ACTM" || props.allowMentions) {
			toolbar = `${toolbar} mentions`;
		}
		if (props.toolBar) {
			toolbar = props.toolBar.join(" ");

			if (!toolbar) return false;
		}
		return toolbar;
	}, []);

	return (
		<Editor
			onInit={(_, editor): void => {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				editorRef.current = editor;
			}}
			disabled={disable}
			inline={inline}
			init={{
				height,
				skin: false,
				content_css: [
					"/tinymce.css",
					props.mentionCss ? "/tinymce-message.css" : "",
				],
				statusbar: false,
				branding: false,
				menubar: false,
				image_advtab: true,
				contextmenu: false,
				icons: "small",
				max_height: props.maxHeight || 300,
				min_height: props.minHeight || 200,
				placeholder: props.placeholder || "",
				images_upload_handler: (blobInfo): Promise<string> =>
					new Promise((resolve, reject) => {
						const info = blobInfo.blob();
						if (mimeTypes.includes(info.type)) {
							const base64 = blobInfo.base64();
							const src = `data:${info.type};base64,${base64}`;
							resolve(src);
						} else {
							reject(new Error("Unsupported type"));
						}
					}),
				plugins:
					"link table lists autoresize preview image paste preview emoticons",
				noneditable_noneditable_class: "mceNonEditable",
				toolbar: getToolBar,
				toolbar_groups: {
					fontstyle: {
						icon: "italic",
						tooltip: "Font Style",
						items: "bold italic underline strikethrough",
					},
					allignment: {
						icon: "align-right",
						tooltip: "Alignment",
						items: "alignleft aligncenter alignright alignjustify",
					},
					lists: {
						icon: "unordered-list",
						tooltip: "Lists",
						items: "numlist bullist",
					},
					font: {
						icon: "text-color",
						tooltip: "Font",
						items: "forecolor fontsize blocks",
					},
				},
				forced_root_block: "p",
				force_p_newlines: true, // Force <p> for new lines
				// Use the paste_preprocess to handle content before pasting
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				paste_preprocess: (_, args): void => {
					// eslint-disable-next-line prefer-destructuring
					let content = args.content;

					// Replace <br> tags with closing and opening <p> tags for paragraphs
					content = content
						.replace(/<\/p>\s*<p>/g, "</p><p>") // Ensure proper paragraph separation
						.replace(/<br\s*\/?>/g, "</p><p>"); // Convert <br> to paragraph breaks

					// Wrap content with <p> if it's not already
					if (!content.startsWith("<p>")) {
						content = `<p>${content}</p>`;
					}

					// eslint-disable-next-line no-param-reassign
					args.content = content;
				},

				setup: (editor): void => {
					editor.on("click", function (e) {
						const link = editor.dom.getParent(e.target, "a");
						if (link) {
							const href = editor.dom.getAttrib(link, "href");
							if (href) {
								window.open(href, "_blank");
								e.preventDefault();
							}
						}
					});
					// Add custom @ button to toolbar
					editor.ui.registry.addButton("mentions", {
						text: "@",
						tooltip: "Insert Mention",
						onAction: () => {
							// Focus the editor
							editor.focus();
							// Insert the "@" symbol at the current cursor position
							editor.insertContent("@");
							editor.execCommand("mceAutocompleterReload");
						},
					});

					// Handle Shift + Enter to insert a paragraph instead of a <br>
					editor.on("keydown", function (e) {
						if (e.key === "Enter" && e.shiftKey) {
							e.preventDefault(); // Prevent the default behavior
							editor.insertContent("<p></p>"); // Insert a new paragraph
						}
					});
					if (customButtons.length) {
						customButtons.forEach(btn => {
							editor.ui.registry.addButton(btn.name, {
								icon: btn.iconName,
								// eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore
								onAction: a => btn.action(editor, a),
								tooltip: btn.tooltip,
							});
						});
					}

					// Updated autocompleter configuration
					editor.ui.registry.addAutocompleter("mentions", {
						trigger: "@",
						minChars: 0,
						columns: 1,

						fetch: pattern =>
							new Promise(resolve => {
								const users = props.mentions;
								// Only filter if there's actually a pattern after @
								const filtered = pattern
									? users?.filter(
											user =>
												user?.label
													?.toLowerCase()
													.includes(pattern.toLowerCase()),
									  )
									: users; // Show all users if just @ is typed
								const results = filtered?.map(user => ({
									type: "autocompleteitem" as const,
									value: user?.value,
									text: user?.label,
									ariaLabel: user?.label,
									icon: ReactDOMServer.renderToString(
										<RecordAvatar
											variant="user"
											title={user?.label}
											size="x-small"
											imageUrl={user?.avatar}
										/>,
									),
								}));

								resolve(results || []);
							}),
						onAction: (autocompleteApi, rng, value) => {
							editor.selection.setRng(rng);
							const selectedUser = props?.mentions?.find(
								user => user?.value === value,
							);
							editor.insertContent(
								`<span class="mention" data-mention-id="${value}" data-mention-name="${selectedUser?.label}">@${selectedUser?.label}</span>&nbsp;`,
							);
							autocompleteApi.hide();
						},
					});

					// Add keydown handler for mention removal
					editor.on("keydown", e => {
						if (e?.key === "Backspace" || e?.key === "Delete") {
							// Backspace or Delete
							const { selection } = editor;
							const node = selection.getNode();

							// Check if we're inside or next to a mention span
							const mentionSpan = node.classList?.contains("mention")
								? node
								: node.closest(".mention");

							if (mentionSpan) {
								e.preventDefault();
								mentionSpan.remove();
								handleEditorChange();
							}
						}
					});
				},

				content_style: [
					contentCss,
					contentUiCss,
					`
					body#tinymce p{
						font-size:16px;
					}
					.mention {
						background-color: transparent;
						padding: 2px 6px;
						border-radius: 4px;
						color: #0991ae;
						text-decoration: none;
						font-weight: 500;
						display: inline-block;
						margin: 0 1px;
						cursor: pointer;
						font-size:14px;
						width: 100%;
    					max-width: max-content;
					}

					.mention:hover {
						background-color: #d0e7ff;
					}
					
					.mce-content-body:not([dir="rtl"])[data-mce-placeholder]:not(
							.mce-visualblocks
						)::before {
						left: unset;
						color: #999999;
						font-size: 12px;
						font-weight: 400;
						top: 14px;
					} 
						
					
					`,
				].join("\n\n"),

				link_assume_external_targets: true,
				// Add these configurations to prevent automatic link conversion
				link_default_target: "_blank",
				link_default_protocol: "https",
				convert_urls: false,
				automatic_uploads: false, // Prevents copy/pasting images overriding by not going into the "image_upload_handler"

				inline_boundaries: false,

				// Prevent automatic URL detection
				autolink_pattern: "",
				autolink: false,
			}}
			onEditorChange={handleEditorChange}
			onBlur={handleBlur}
			value={value?.html || value?.text}
		/>
	);
}

export default TinyMceInput;
